/**
 * Turns what the Add enquiry form holds into the body the create endpoint
 * takes. The mirror of `enquiryMapping`, which goes the other way.
 *
 * The work here is all in the gap between the two: the form holds the *names*
 * its dropdowns display, and the API takes the *ids* its catalog lists carry.
 * Resolving one to the other needs the lists themselves, which is why this
 * takes the lookups rather than the enquiry alone.
 *
 * The client form's own version of this is `clientPayload`, and the two field
 * rules that are genuinely the same — a date of birth, an enum constant — are
 * imported from it rather than written again.
 */

import { toEnumConstant, toIsoDate } from "../clients/clientPayload";
import { toEnquirySource } from "./enquiryMapping";
import type { CatalogLookup } from "../clients/hooks/useCatalogLookups";
import type { EnquiryLookups } from "./hooks/useEnquiryLookups";
import type {
  CreateEnquiryPayload,
  EnquiryRecord,
  NewEnquiry,
} from "./types/enquiries.types";

/**
 * The API's `YYYY-MM-DD` back into the DD/MM/YYYY the form is typed in — the
 * mirror of `toIsoDate`, and what fills the DOB field when the edit form
 * opens. A record with no date of birth opens on an empty field.
 */
export const toFormDate = (isoDate: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec((isoDate ?? "").trim());
  if (!match) return "";

  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

/**
 * A stored enquiry back into the answers the form holds — what the edit page
 * opens on.
 *
 * The record already carries the names its dropdowns display rather than the
 * ids underneath, so most fields go across as they are; only the date and the
 * WhatsApp checkbox are worked out. Nothing is defaulted or invented: a field
 * the backend has nothing for opens empty, and the form's own validation is
 * what decides whether that can be saved.
 */
export const toEnquiryForm = (record: EnquiryRecord): NewEnquiry => ({
  title: record.title,
  firstName: record.firstName,
  lastName: record.lastName,
  gender: record.gender,
  dob: toFormDate(record.dateOfBirth),
  profession: record.profession,
  lifestyle: record.lifestyle,
  goals: record.goals,
  enquiryFrom: record.mode,
  address: record.fullAddress,
  state: record.state,
  city: record.city,
  pincode: record.postalCode,
  phone: record.phone,
  whatsapp: record.whatsapp,
  // Ticked when the two already match, which is what the box means — and what
  // keeps them moving together if the phone is edited from here.
  whatsappSameAsPhone: !!record.phone && record.phone === record.whatsapp,
  email: record.email,
});

/**
 * The id behind a displayed name, or `null` when the list has no such row.
 *
 * Numeric only: this endpoint takes catalog ids, and every list an enquiry
 * resolves against is one of the backend's catalogs. `LookupOption` also
 * allows the `public_id` string a practitioner service carries — the client
 * form's Goal field picks from those — and one of those reaching here would be
 * an id this endpoint cannot read, so it is reported unresolved rather than
 * sent.
 */
const idOf = (lookup: CatalogLookup, name: string): number | null => {
  const id = lookup.options.find((option) => option.name === name)?.id;
  return typeof id === "number" ? id : null;
};

/**
 * The `public_id` behind a service's name, for the Goal field — which picks
 * from the practice's own active services rather than from a catalog, and
 * sends what it picked by that id.
 *
 * A row carrying a number is a catalog row rather than a service, so it does
 * not resolve: the endpoint would read the id as another record entirely.
 */
const serviceIdOf = (lookup: CatalogLookup, name: string): string | null => {
  const id = lookup.options.find((option) => option.name === name)?.id;
  return typeof id === "string" ? id : null;
};

/**
 * Either a body ready to post, or the fields whose names could not be matched
 * to a row in their list.
 *
 * That second case should not happen — the dropdowns only offer names that
 * came from the lists — but it becomes reachable the moment a list is edited
 * on the backend while the form sits open. Better a named failure the screen
 * can report than an id silently sent as `null`.
 */
export type EnquiryPayloadResult =
  | { ok: true; payload: CreateEnquiryPayload }
  | { ok: false; unresolved: string[] };

/**
 * The reference fields that hold one answer, each with the list it resolves
 * against. Goals are not here — they hold several, and are resolved as a list
 * of their own below.
 */
const SINGLE_REFERENCE_FIELDS = [
  { label: "Life style", key: "lifeStyles" },
] as const;

/**
 * Title, which is no longer among them because it is no longer asked for.
 *
 * The check the list above performs is "a name was picked and its row has
 * gone", which is a real failure worth refusing a save over. An unpicked title
 * is not that — it is the ordinary case now — and running it through the same
 * test would report every enquiry saved without one as a catalog that had
 * moved underneath the form.
 *
 * So the two are told apart here. Blank means leave the key off the body;
 * picked-but-unresolvable still fails the build, exactly as a life style does.
 */
const titleIdOf = (
  lookups: EnquiryLookups,
  name: string
): { omit: true } | { omit: false; id: number | null } =>
  name.trim() ? { omit: false, id: idOf(lookups.titles, name) } : { omit: true };

export const toCreateEnquiryPayload = (
  enquiry: NewEnquiry,
  lookups: EnquiryLookups
): EnquiryPayloadResult => {
  const title = titleIdOf(lookups, enquiry.title);
  const ids = {
    lifeStyles: idOf(lookups.lifeStyles, enquiry.lifestyle),
  };

  // Every goal picked, each resolved on its own. One that no longer matches a
  // row fails the whole build rather than being quietly dropped — an enquiry
  // saved against a goal it was not raised for is worse than a refused save.
  const goalIds = enquiry.goals.map((name) =>
    serviceIdOf(lookups.serviceGoals, name)
  );

  const unresolved = [
    ...(!title.omit && title.id === null ? ["Title"] : []),
    ...SINGLE_REFERENCE_FIELDS.filter(({ key }) => ids[key] === null).map(
      ({ label }) => label
    ),
    ...(goalIds.some((id) => id === null) ? ["Goal"] : []),
  ];

  if (unresolved.length > 0) return { ok: false, unresolved };

  return {
    ok: true,
    payload: {
      // Left off the body where it was not picked, rather than sent as a null
      // or a nought. This field takes a catalog id, and both of those are a
      // claim about the person: one that no title fits them, one that some row
      // does. An absent key says neither, which is the truth — and it is the
      // same answer the client form's own `toSubscriptionFields` gives.
      ...(title.omit ? {} : { title: title.id as number }),
      first_name: enquiry.firstName.trim(),
      last_name: enquiry.lastName.trim(),
      gender: toEnumConstant(enquiry.gender),
      // The same treatment, for the same reason: `toIsoDate` answers an empty
      // field with an empty string, and this column takes a date.
      ...(enquiry.dob.trim() ? { date_of_birth: toIsoDate(enquiry.dob) } : {}),
      profession: enquiry.profession,
      goals: goalIds as string[],
      life_style: ids.lifeStyles as number,
      enquiry_source: toEnquirySource(enquiry.enquiryFrom),
      full_address: enquiry.address.trim(),
      state: enquiry.state,
      city: enquiry.city,
      postal_code: enquiry.pincode,
      // The ten digits as collected — this endpoint adds the dialling code
      // itself, unlike the client create. See `CreateEnquiryPayload`.
      phone_number: enquiry.phone,
      whatsapp_number: enquiry.whatsapp,
      email: enquiry.email.trim(),
    },
  };
};
